import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { useNavigate } from 'react-router-dom';

import { memoService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useMemoStore } from '@/stores';
import type { Memo } from '@/types';

export interface SourceQuotePendingAction {
    type: 'create-memo' | 'add-to-memo' | 'copy-quote' | 'copy-source';
    memoId?: string;
}

interface UseSourceQuoteActionsOptions {
    getQuoteHtml: (quote: string) => string;
    getSourceLink: (quote?: string) => string;
    memoTitle: string;
    selectedText?: string;
    sourceKey: string;
}

/** Keep every action surface in one flight and preserve the source selected at submission. */
export const useSourceQuoteActions = ({
    getQuoteHtml,
    getSourceLink,
    memoTitle,
    selectedText,
    sourceKey,
}: UseSourceQuoteActionsOptions) => {
    const [pendingAction, setPendingAction] = useState<SourceQuotePendingAction>();
    const inFlight = useRef(false);
    const mounted = useRef(true);
    const activeSourceKey = useRef(sourceKey);
    const navigate = useNavigate();
    const { createMemo, updateMemo } = useMemoStore();

    useLayoutEffect(() => {
        activeSourceKey.current = sourceKey;
    }, [sourceKey]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const runAction = useCallback(
        async (action: SourceQuotePendingAction, operation: () => Promise<void>, errorMessage: string) => {
            if (inFlight.current) return;
            inFlight.current = true;
            setPendingAction(action);
            try {
                await operation();
            } catch (error) {
                captureException(error, { tags: { action: `source-reader.${action.type}` } });
                toast.error(errorMessage);
            } finally {
                inFlight.current = false;
                if (mounted.current) setPendingAction(undefined);
            }
        },
        [],
    );

    const createMemoFromQuote = useCallback(async () => {
        if (!selectedText) return;
        const body = getQuoteHtml(selectedText);
        await runAction(
            { type: 'create-memo' },
            async () => {
                const memo = await createMemo({ title: memoTitle, body });
                if (!memo?.id) {
                    // The store already reports the provider error and rolls back its optimistic Memo.
                    toast.error('Failed to create Memo from quote');
                    return;
                }
                toast.success('Memo created from source quote');
                if (mounted.current && activeSourceKey.current === sourceKey) navigate(`/memos/${memo.id}`);
            },
            'Failed to create Memo from quote',
        );
    }, [createMemo, getQuoteHtml, memoTitle, navigate, runAction, selectedText, sourceKey]);

    const appendQuoteToMemo = useCallback(
        async (memo: Memo) => {
            if (!memo.id || !selectedText) return;
            const quoteHtml = getQuoteHtml(selectedText);
            await runAction(
                { type: 'add-to-memo', memoId: memo.id },
                async () => {
                    const fullMemo = await memoService.getById(memo.id);
                    const body = fullMemo.body ?? memo.body ?? '';
                    await updateMemo(
                        memo.id,
                        { body: `${body.trim() ? `${body}<p></p>` : ''}${quoteHtml}` },
                        { throwOnError: true },
                    );
                    toast.success('Source quote added to Memo', {
                        description: fullMemo.title || memo.title || 'Untitled Memo',
                    });
                },
                'Failed to add source quote to Memo',
            );
        },
        [getQuoteHtml, runAction, selectedText, updateMemo],
    );

    const copyQuote = useCallback(async () => {
        if (!selectedText) return;
        const text = `${selectedText}\n\nSource: ${getSourceLink(selectedText)}`;
        await runAction(
            { type: 'copy-quote' },
            async () => {
                await navigator.clipboard.writeText(text);
                toast.success('Source quote copied');
            },
            'Failed to copy source quote',
        );
    }, [getSourceLink, runAction, selectedText]);

    const copySourceLink = useCallback(async () => {
        const link = getSourceLink(selectedText);
        await runAction(
            { type: 'copy-source' },
            async () => {
                await navigator.clipboard.writeText(link);
                toast.success('Source link copied');
            },
            'Failed to copy source link',
        );
    }, [getSourceLink, runAction, selectedText]);

    return {
        appendQuoteToMemo,
        copyQuote,
        copySourceLink,
        createMemoFromQuote,
        pendingAction,
        busy: Boolean(pendingAction),
    };
};
